import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Nfc, Usb, CreditCard, AlertTriangle, RadioTower } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const BRIDGE_URL =
  (typeof window !== "undefined" && localStorage.getItem("nfc_bridge_url")) || "http://127.0.0.1:8899";

// Descrizione visiva di ogni stato del flusso
const STATES = {
  checking: { icon: Loader2, spin: true, color: "text-slate-500 bg-slate-100", label: "Ricerca lettore NFC..." },
  "no-reader": { icon: Usb, color: "text-rose-600 bg-rose-100", label: "Lettore NFC non rilevato" },
  "no-card": { icon: CreditCard, color: "text-amber-600 bg-amber-100", label: "Carta non presente" },
  card: { icon: RadioTower, color: "text-emerald-600 bg-emerald-100", label: "Carta rilevata" },
  writing: { icon: Loader2, spin: true, color: "text-indigo-600 bg-indigo-100", label: "Scrittura in corso..." },
  verifying: { icon: Loader2, spin: true, color: "text-indigo-600 bg-indigo-100", label: "Verifica in corso..." },
  done: { icon: Check, color: "text-emerald-600 bg-emerald-100", label: "Carta NFC scritta e verificata correttamente ✅" },
  error: { icon: AlertTriangle, color: "text-rose-600 bg-rose-100", label: "Errore durante la scrittura" },
};

export default function NfcModal({ card, open, onOpenChange, onWritten }) {
  const [state, setState] = useState("checking");
  const [error, setError] = useState("");
  const [readerName, setReaderName] = useState("");
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  // Polling dello stato del lettore/carta mentre la finestra è aperta
  useEffect(() => {
    if (!open) return;
    setState("checking");
    setError("");
    let active = true;
    const poll = async () => {
      if (["writing", "verifying", "done"].includes(stateRef.current)) return;
      try {
        const r = await fetch(`${BRIDGE_URL}/status`, { cache: "no-store" });
        const d = await r.json();
        if (!active) return;
        setReaderName(d.reader_name || "");
        if (!d.reader) setState("no-reader");
        else if (!d.card) setState("no-card");
        else setState("card");
      } catch {
        if (active) setState("no-reader");
      }
    };
    poll();
    const iv = setInterval(poll, 1500);
    return () => { active = false; clearInterval(iv); };
  }, [open]);

  if (!card) return null;

  const start = async () => {
    setError("");
    setState("writing");
    try {
      const r = await fetch(`${BRIDGE_URL}/write`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: card.public_url }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || "Scrittura fallita");
      setState("verifying");
      await new Promise((res) => setTimeout(res, 500));
      // Persiste lo stato "NFC scritto" nel database (non bloccante)
      try {
        const res = await api.post(`/cards/${card.id}/nfc-write`);
        onWritten?.(res.data);
      } catch (_) { /* stato DB non bloccante */ }
      setState("done");
      toast.success("Carta NFC scritta e verificata correttamente ✅");
    } catch (e) {
      setState("error");
      setError(e.message || "Scrittura fallita");
      toast.error(e.message || "Scrittura fallita");
    }
  };

  const close = (v) => {
    if (!v) { setState("checking"); setError(""); }
    onOpenChange(v);
  };

  const s = STATES[state] || STATES.checking;
  const Icon = s.icon;
  const canWrite = state === "card";
  const busy = state === "writing" || state === "verifying";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="modal-nfc-write" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Nfc size={20} /> Scrivi carta NFC</DialogTitle>
        </DialogHeader>
        <div className="pt-2 text-sm text-slate-600 space-y-4">
          <p className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            Verrà scritto questo URL permanente sul chip:
            <span className="mt-1 block font-mono text-xs text-indigo-600 break-all">{card.public_url}</span>
          </p>

          {/* Stato in tempo reale */}
          <div data-testid="nfc-status" data-state={state}
            className={`flex items-center gap-3 rounded-xl p-3 font-medium ${s.color}`}>
            <Icon size={22} className={s.spin ? "animate-spin" : ""} />
            <span>{state === "error" ? (error || s.label) : s.label}</span>
          </div>

          {state !== "no-reader" && state !== "checking" && (
            <p data-testid="nfc-reader-indicator" className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
              <Check size={14} /> Lettore ACR1552 collegato{readerName ? ` (${readerName})` : ""}
            </p>
          )}

          {state === "no-reader" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              Avvia il <strong>TapCard NFC Bridge</strong> sul Mac (cartella <code>nfc-bridge</code> →
              <code> ./run.command</code>), poi collega il lettore ACR1552. Il bridge deve essere in ascolto su
              <code> 127.0.0.1:8899</code>.
            </div>
          )}

          {state === "done" ? (
            <div className="flex flex-col items-center gap-2 py-2 text-emerald-600">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100"><Check size={30} /></div>
              <p className="text-center font-semibold">Carta NFC scritta e verificata correttamente ✅</p>
            </div>
          ) : state === "error" ? (
            <Button data-testid="btn-start-nfc-write" onClick={start}
              className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-base">
              <Nfc className="mr-2" size={18} /> Riprova scrittura
            </Button>
          ) : (
            <Button data-testid="btn-start-nfc-write" onClick={start} disabled={!canWrite || busy}
              className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-base disabled:opacity-50">
              {busy ? (
                <><Loader2 className="mr-2 animate-spin" size={18} /> {state === "verifying" ? "Verifica in corso..." : "Scrittura in corso..."}</>
              ) : (
                <><Nfc className="mr-2" size={18} /> Inizia scrittura NFC</>
              )}
            </Button>
          )}
          {!canWrite && !busy && state !== "done" && state !== "error" && state !== "no-reader" && (
            <p className="text-center text-xs text-slate-400">Appoggia una carta NFC sul lettore per abilitare la scrittura.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
